'use client'

import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'

import { todayInTimeZone, type DisplayDateAnchor } from '@/i18n/dates'

// Type-only in the other direction (`import { type WidgetState }`), so this pair does
// not form a runtime cycle.
import { readSharedSelection } from './shared-search-store'

export interface WidgetState {
    from: string
    fromId: string
    to: string
    toId: string
    departDate: string
    returnDate?: string
    mode: 'bus' | 'train' | 'ferry' | 'flight'
    tripType: 'one-way' | 'round-trip'
    /** Seat count (1–8). Mirrors ui-react's multi-pax `?p=N` search param. */
    passengers: number
}

/** A selection and its updater — what an owner holds and a caller may hand downward. */
export type WidgetStateTuple = readonly [WidgetState, (action: SetStateAction<WidgetState>) => void]

const defaultState: WidgetState = {
    from: '',
    fromId: '',
    to: '',
    toId: '',
    departDate: '',
    mode: 'bus',
    tripType: 'one-way',
    passengers: 1,
}

/** Today (YYYY-MM-DD) in the browser's own local zone. Post-mount only — reading
 *  the ambient clock at render would break hydration (invariants.md#ssr-safe-rendered-dates). */
function getLocalToday(): string {
    const d = new Date()
    const y = String(d.getFullYear())
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

/** The day after a `YYYY-MM-DD` string, as `YYYY-MM-DD`. A pure function of the input
 *  (noon-local construction handles month/year rollover and DST), so the "tomorrow"
 *  floor never reads the ambient clock and stays SSR-safe when derived from the
 *  server anchor (invariants.md#ssr-safe-rendered-dates). */
function nextDay(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number)
    const dt = new Date(y, m - 1, d + 1, 12, 0, 0)
    const yy = String(dt.getFullYear())
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const dd = String(dt.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
}

/**
 * While establishing the selection, fill an **empty or past** departDate with
 * **tomorrow** (the day after the caller-supplied `today`) and drop a returnDate that
 * falls before it. The widget never *defaults* to a same-day departure — a same-day
 * trip is largely unbookable or irrelevant late in the day (RD-6) — but an existing
 * **same-day** date is preserved: it is a valid selection the user (or a restored
 * prior search) chose deliberately, and re-flooring it here would silently rewrite a
 * saved search on remount. "Tomorrow" is derived from the `today` string, so the
 * fill stays a pure function of the anchor.
 *
 * This runs at mount and on a restore/initial merge, never on a subsequent field
 * change — the "today" shortcut (FR-24) goes through `setState` and sets a same-day
 * departure this pass then also leaves intact on the next remount.
 *
 * `today` is **required but nullable** with **no ambient-clock fallback** — every
 * caller must pass it explicitly, so forgetting it is a compile error rather than a
 * silent no-clamp. The SSR / first-client-render path passes `anchor?.today` — a
 * fixed, server-computed string — which is `undefined` when a caller mounts the
 * widget without a `DisplayDateAnchor`. In that case we must NOT read the wall clock
 * at render: that is exactly the React #418 hydration hazard the anchor exists to
 * prevent (invariants.md#ssr-safe-rendered-dates), and a dropped `dateAnchor` prop
 * would otherwise silently reintroduce it. The post-mount effect always passes a
 * concrete `today` (origin- or browser-anchored) and does the clamp then, so a
 * missing anchor degrades to an un-clamped depart date the effect fills in —
 * never to a clock-dependent render.
 */
function adjustDates(state: WidgetState, today: string | undefined): WidgetState {
    if (today) {
        if (!state.departDate || state.departDate < today) {
            state = { ...state, departDate: nextDay(today) }
        }
    }

    if (state.returnDate && state.returnDate < state.departDate) {
        state = { ...state, returnDate: undefined }
    }

    return state
}

/** Enforce returnDate >= departDate on every state update */
function enforceReturnDate(state: WidgetState): WidgetState {
    if (state.returnDate && state.departDate && state.returnDate < state.departDate) {
        return { ...state, returnDate: state.departDate }
    }
    return state
}

/**
 * Drop keys whose value is `undefined` so a partial object passed by a caller
 * (e.g. `{ from: undefined }` from a widget mounted with no initial values)
 * doesn't clobber the string defaults during a spread merge.
 */
function definedValues<T extends object>(obj: T | undefined): Partial<T> {
    if (!obj) {
        return {}
    }
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined),
    ) as Partial<T>
}

/** Field-wise equality, so the post-mount re-anchor can skip a no-op re-render. */
function widgetStateEqual(a: WidgetState, b: WidgetState): boolean {
    return (
        a.from === b.from &&
        a.fromId === b.fromId &&
        a.to === b.to &&
        a.toId === b.toId &&
        a.departDate === b.departDate &&
        a.returnDate === b.returnDate &&
        a.mode === b.mode &&
        a.tripType === b.tripType &&
        a.passengers === b.passengers
    )
}

/**
 * The client's effective "today" once hydrated (invariants.md#ssr-safe-rendered-dates):
 *  - a known origin zone wins on both server and client, so the depart-date stays
 *    pinned to the origin and never drifts to the browser clock (rule 3);
 *  - with no origin, the user's own local day wins (rule 4).
 */
function clientToday(anchor: DisplayDateAnchor | undefined): string {
    return anchor?.originTimeZone ? todayInTimeZone(anchor.originTimeZone) : getLocalToday()
}

export interface UseWidgetStateOptions {
    /**
     * `false` when the caller renders a selection it got from somewhere else — a
     * `widgetState` prop or the surface-owned selection — and discards this hook's
     * value. A disabled instance does not restore: reading the store on behalf of a
     * selection nobody renders is pure waste.
     *
     * Constant for the lifetime of an instance in every current caller (a provider
     * either encloses a widget or does not; the `widgetState` prop is stable), so the
     * restore effect re-running on a change is not a path anything exercises.
     */
    enabled?: boolean
}

export function useWidgetState(
    initial?: Partial<WidgetState>,
    anchor?: DisplayDateAnchor,
    options?: UseWidgetStateOptions,
): WidgetStateTuple {
    const initialRef = useRef(initial)
    const anchorRef = useRef(anchor)
    const enabled = options?.enabled ?? true

    const [state, rawSetState] = useState<WidgetState>(() => {
        // Server-safe: defaults + initial props, anchored to the server-computed
        // `anchor.today` so SSR and first client render agree (no ambient clock at
        // render) — invariants.md#ssr-safe-rendered-dates. With no anchor the date
        // is left un-clamped rather than read from the clock; the post-mount
        // re-anchor below then applies the origin zone (rule 3) or the user's zone
        // (rule 4).
        return adjustDates({ ...defaultState, ...definedValues(initial) }, anchor?.today)
    })

    const setState = useCallback((updater: SetStateAction<WidgetState>) => {
        rawSetState((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater
            return enforceReturnDate(next)
        })
    }, [])

    // After mount, re-anchor to the client's effective "today" and merge the visitor's
    // last submitted search. Runs unconditionally (not just when a stored search
    // exists) so rule 4 — the user's own zone winning on an originless surface — takes
    // effect even for a first-time visitor. Re-anchoring to the origin zone (rule 3)
    // also stops a restored date from drifting to the browser clock. The equality guard
    // skips the re-render when nothing changed.
    //
    // Read-only, by design: nothing here writes. The store belongs to the legacy
    // booking application and only a submission may enter it (§5, RD-9), so a
    // surface's initial values — page context, not the visitor's choice — can no
    // longer displace a real search.
    useEffect(() => {
        if (!enabled) {
            return
        }

        const today = clientToday(anchorRef.current)
        const stored = readSharedSelection()
        const base = { ...defaultState, ...stored, ...definedValues(initialRef.current) }
        rawSetState((prev) => {
            const next = adjustDates(base, today)
            return widgetStateEqual(prev, next) ? prev : next
        })
    }, [enabled])

    return [state, setState] as const
}
